import type Distance from "@/model/distance";
import type Pos from "@/model/pos";
import ScreenRouteNames from "./screen-route-names";

export const EMPTY_VALUE = "N/A";

export const enum SortOptions {
  COST = "cost",
  DISTANCE = "distance",
}

export const enum DownloadOptions {
  FIRST_10 = "10",
  FIRST_50 = "50",
  FIRST_100 = "100",
}

export const enum ModalIdentifiers {
  DISCLAIMER = "disclaimer",
  COLLAPSED_COST_BREAKDOWN_CONTAINER = "collapsed-cost-breakdown-container",
  POS_COST_BREAKDOWN_CONTAINER = "pos-cost-breakdown-container",
  PROVIDER_INFO = "provider-info",
  SPEND_SUMMARY = "spend-summary",
}

export const ALL_POS: Pos[] = [
  {
    id: 1,
    name: "PHARMACY",
    displayName: "Pharmacy",
    code: 1,
  },
  {
    id: 2,
    name: "TELEMEDICINE",
    displayName: "Telemedicine",
    code: 2,
  },
  {
    id: 3,
    name: "OUTPATIENT",
    displayName: "Outpatient",
    code: 3,
  },
  {
    id: 4,
    name: "OFFICE",
    displayName: "Office",
    code: 4,
  },
  {
    id: 5,
    name: "IN_PATIENT",
    displayName: "Inpatient",
    code: 5,
  },
  {
    id: 6,
    name: "EMERGENCY_ROOM",
    displayName: "Emergency Room",
    code: 6,
  },
  {
    id: 7,
    name: "OTHERS",
    displayName: "Others",
    code: 7,
  },
];

export const DISTANCE_OPTIONS: Distance[] = [
  {
    id: 1,
    value: 5,
    displayName: "5 Miles",
  },
  {
    id: 2,
    value: 10,
    displayName: "10 Miles",
  },
  {
    id: 3,
    value: 20,
    displayName: "20 Miles",
  },
  {
    id: 4,
    value: 50,
    displayName: "50 Miles",
  },
  {
    id: 5,
    value: 100,
    displayName: "100 Miles",
  },
  {
    id: 6,
    value: 200,
    displayName: "200 Miles",
  },
];
export const DEFAULT_DISTANCE = DISTANCE_OPTIONS[2];

/**
 * HCBB's own radius options — a different scale (5/10/25/50/75/100) from the
 * legacy Cost Estimator's `DISTANCE_OPTIONS` above, so it needs its own list
 * rather than a subset or relabeling of that one.
 */
export const HCBB_DISTANCE_OPTIONS: Distance[] = [
  {
    id: 1,
    value: 5,
    displayName: "5 Miles",
  },
  {
    id: 2,
    value: 10,
    displayName: "10 Miles",
  },
  {
    id: 3,
    value: 25,
    displayName: "25 Miles",
  },
  {
    id: 4,
    value: 50,
    displayName: "50 Miles",
  },
  {
    id: 5,
    value: 75,
    displayName: "75 Miles",
  },
  {
    id: 6,
    value: 100,
    displayName: "100 Miles",
  },
];
export const HCBB_DEFAULT_DISTANCE = HCBB_DISTANCE_OPTIONS[3];
export const DEFAULT_LIMIT = DownloadOptions.FIRST_10 as DownloadOptions;
export const DEFAULT_SORT = SortOptions.DISTANCE as SortOptions;
export const DEFAULT_TIER = [{ name: "TIER_1" }];

// export const PROVIDER_SEARCH_DISTANCE: Distance[] = [
//   {
//     id: 1,
//     value: 5,
//     displayName: "5 Miles",
//   },
//   {
//     id: 2,
//     value: 10,
//     displayName: "10 Miles",
//   },
//   {
//     id: 3,
//     value: 20,
//     displayName: "20 Miles",
//   },
//   {
//     id: 4,
//     value: 50,
//     displayName: "50 Miles",
//   },
//   {
//     id: 5,
//     value: 100,
//     displayName: "100 Miles",
//   },
//   {
//     id: 6,
//     value: null,
//     displayName: "Anywhere",
//   },
// ];

export const enum ScreeRouteNames {
  ROOT = "/",
  GROUP = "group",
  DISCLAIMER = "disclaimer",
  LOCATE = "locate",
  LOGIN = "login",
  GUEST_LOGIN = "guest-login",
  REGISTER = "register",
  ACCOUNT_RESET = "account-reset",
  VERIFICATION = "verification",
  VERIFY_OTP = "verify-otp",
  MY_BENEFIT = "my-benefit",
  PROVIDER_INFO = "provider-info",
  SPEND_SUMMARY = "spend-summary",
  PROVIDER_SEARCH = "provider-search",
  PROVIDER_SEARCH_PT = "provider-search-pt",
  PROFILE = "profile",
  ERROR = "error",
  HOME = "home",
  NOTIFICATION = "notification",
  CARE_HISTORY = "care-history",
  ID_CARD = "id-card",
  SERVICE = "service",
  BIOMETRICS = "biometrics",
  MY_PCP = "my-pcp",
  MY_ACCOUNT = "my-account",
  COST_ESTIMATOR = "cost-estimator",
  COST_ESTIMATOR_RESULT = "results",
}

export const GROUP_CODE_MIN_LENGTH = 3;
export const GROUP_CODE_MAX_LENGTH = 25;

export const USERNAME_MIN_LENGTH = 8;
export const USERNAME_MAX_LENGTH = 20;

export const OTP_TIMEOUT = 120;

export enum ResponseCode {
  SUCCESS = 200,
  UNAUTHORIZED = 401,
  NOT_FOUND = 404,
  SERVER_ERROR = 500,
  BAD_REQUEST = 400,
  FORBIDDEN = 403,
  NETWORK_TIME_OUT = "ECONNABORTED",
}

export enum AUTH_TYPE {
  REST = "REST",
  SAML = "SAML",
}

export enum QueryKeys {
  MEMBERS = "members",
  MEMBERS_LOOKUP = "members-lookup",
  DEMOGRAPHICS = "demographics",
  WIDGETS = "widgets",
  COURT_ORDER_DEPENDENTS = "courtOrderDependents",
  GLOBAL_FLAGS = "global-flags",
  PROVIDER_NETWORK_TYPE = "provider-network-type",
  DEPENDENTS = "dependents",
  MEMBER_SERVICE = "member-service",
  CARE_HISTORY_CONFIG = "care-history-config",
  CARE_HISTORY_GRAPH = "care-history-graph",
  CARE_HISTORY_CLAIMS = "care-history-claims",
  EOB = "eob",
  ID_CARD = "id-card",
  NETWORKS = "networks",
  SPECIALITIES = "specialities",
  BENEFIT_BREAKDOWN = "benefit-breakdown",
  SPEND_SUMMARY = "spend-summary",
  PROVIDER_COST = "provider-cost",
  COVERAGE_YEARS = "coverage-years",
  PROCEDURE_SEARCH = "procedure-search",
  HCBB_SEARCH = "hcbb-search",
  HCBB_ESTIMATE = "hcbb-estimate",
  HCBB_PROFESSIONALS = "hcbb-professionals",
  /**
   * Per-professional cache entry (singular, distinct from HCBB_PROFESSIONALS'
   * per-batch entries) — written by every batch fetch regardless of grouping,
   * so a professional fetched under one batch composition is still known
   * when a filtered candidate list re-groups it into a different batch.
   */
  HCBB_PROFESSIONAL = "hcbb-professional",
  HCBB_FACILITY_PROVIDERS = "hcbb-facility-providers",
  HCBB_RECENT_SEARCH = "hcbb-recent-search",
  LOCATION_SEARCH = "location-search",
  PROVIDER_SEARCH = "provider-search",
  FACILITY_SEARCH = "facility-search",
  PROVIDER_COST_SEARCH = "provider-cost-search",
  NOTIFICATIONS = "notifications",
  RECOMMENDATIONS = "recommendations",
  READ_NOTIFICATION = "read-notification",
  UPDATE_RECOMMENDATION = "update-recommendation",
  DATA_ACCESS_CONFIG = "data-access-config",
  DATA_ACCESS_LIST = "data-access-list",
  UPDATE_ACCOUNT_INFO = "update-account-info",
  UPDATE_ACCOUNT_OTP = "update-account-otp",
  CHANGE_PASSWORD = "change-password",
  PASSWORD_RESET = "password-reset",
  FORGET_USERNAME = "forget-username",
  FORGET_PASSWORD_OTP = "forget-password-otp",
  GROUP = "group",
  LOGIN = "login",
  REGISTER = "register",
  REGISTER_OTP = "register-otp",
  PROVIDER_SEARCH_SETTING = "provider-search-setting",
  PROVIDER_SEARCH_FREE_TEXT = "provider-search-freetext",
  PROVIDER_SEARCH_FREE_TEXT_VIEW_MORE = "provider-search-freetext-viewmore",
  PROVIDER_SEARCH_RESULT = "provider-search-result",
  MY_BENEFITS = "my-benefits",
  MFA_SETUP_OTP = "mfa-setup-otp",
  MFA_SETUP_STATUS = "mfa-setup-status",
  LOGIN_MFA = "login-mfa",
  LOGIN_MFA_RESEND_OTP = "login-mfa-resend-otp",
  MFA_DISABLE = "disable-mfa",
  MENU_LIST = "menu-list",
  FEATURE_LIST = "feature-list",
  EBIX = "ebix",
  HCCB = "hcbb",
  FETCH_SURVEY_QUESTIONNAIRE = "fetch-survey-questionnaire",
  SUBMIT_SURVEY_FEEDBACK = "submit-survey-feedback",

  CHATBOT_DEFAULT_QUERIES = "chatbot-default-queries",
  FEEDBACK = "feedback",
  BALANCE_BILL_LIST = "balance-bill-list",
  BALANCE_BILL_UPLOAD = "balance-bill-upload",
  BALANCE_BILL_SINGLE_ATTACHMENT = "balance-bill-single-attachment",
  FEATURE_IMAGES = "feature-images",
  FEATURE_IMAGE = "feature-image",
  OLD_COST_ESTIMATOR = "old-cost-estimator",
  COVERAGE_CERTIFICATE = "coverage-certificate",
  REQUEST_ID_CARD = "request-id-card",
  UI_LABELS = "ui-labels",
  PAPERLESS_EOB_FETCH = "paperless-eob-fetch",
  PAPERLESS_EOB_UPDATE = "paperless-eob-update",
  TAX_FORM_FETCH = "tax-form-fetch",
  RATING = "rating",
  PCP_INFO = "pcpInfo",
  VIDEO_LIST = "video-list",
}

export enum NotificationStatus {
  OPEN = "OPENED",
  SAVE = "SAVED",
  UNSAVE = "UNSAVED",
  LIKE = "LIKED",
  UNLIKE = "UNLIKED",
}

export enum NotificationActionButtonType {
  APP_ROUTE = "appRoute",
  WEB_LINK = "webLink",
  PHONE = "phone",
  EMAIL = "email",
  MAP = "map",
}

export enum GROUP_ID {
  SOMEHA = "SHOWMEHA",
}

export enum CLAIMS_STATUS {
  PAID = "Paid",
  REVERSED = "Reversed",
  IN_PROCESS = "In Process",
  PENDING = "Pending",
  RECEIVED = "Received",
  OTHERS = "Others",
}

export const CARE_HISTORY_SELF_GRAPH_COLOR = "#006895";

export const CARE_HISTORY_FAMILY_GRAPH_COLOR = "#FFA500";

export const CARE_HISTORY_FAMILY_MEMBER_GRAPH_COLOR = [
  "#FF5733",
  "#33FF57",
  "#3357FF",
  "#FF33A8",
  "#A833FF",
  "#33FFF5",
  "#F5FF33",
  "#FF8C33",
  "#8C33FF",
  "#33FF8C",
  "#FF3333",
  "#33A8FF",
];

export const MONTH_NUMBER_TO_LABEL_MAPPER: Record<string, string> = {
  "01": "Jan",
  "02": "Feb",
  "03": "Mar",
  "04": "Apr",
  "05": "May",
  "06": "Jun",
  "07": "Jul",
  "08": "Aug",
  "09": "Sep",
  "10": "Oct",
  "11": "Nov",
  "12": "Dec",
};

export const CARE_HISTORY_PERIOD_TYPE = [
  {
    id: 1,
    displayName: "Monthly",
    value: "M",
  },
  {
    id: 2,
    displayName: "Year To Date",
    value: "Y",
  },
];

export enum CARE_HISTORY_PERIOD_FLAG {
  MONTHLY = "M",
  YEAR_TO_DATE = "Y",
}

export const CARE_HISTORY_OOP = {
  TOTAL_OOP_PAID: "allOopPaid",
  PHARMACY_OOP_PAID: "pharmacyOopPaid",
  MEDICAL_OOP_PAID: "providerOopPaid",
};

export const CLAIM_TYPES = {
  ALL: {
    LABEL: "Both",
    VALUE: "All",
  },
  MEDICAL: {
    LABEL: "Medical Only",
    VALUE: "Provider",
  },
  PHARMACY: {
    LABEL: "Pharmacy Only",
    VALUE: "Pharmacy",
  },
};

export enum CLAIMS_TYPE {
  PROVIDER = "Provider",
  PHARMACY = "Pharmacy",
  MEDICAL = "MED",
}

export const CLAIMS_FILTER_OPTIONS = {
  CLAIM_NUMBER: {
    LABEL: "Claim Number Only",
    VALUE: "Claim Number",
  },
  ADVANCED_SEARCH: {
    LABEL: "Advanced Search",
    VALUE: "Advanced Search",
  },
};

export enum CLAIM_TIME_PERIOD {
  ALL = "All",
  DATE_RANGE = "Date Range",
}

export const CLAIMS_SORT_BY = {
  DATE_OF_SERVICE_NEW_TO_OLD: {
    VALUE: "date-",
    LABEL: "Date of Service: New to Old",
  },
  DATE_OF_SERVICE_OLD_TO_NEW: {
    VALUE: "date+",
    LABEL: "Date of Service: Old to New",
  },
};

export const ACCUMULATOR_YEAR_DROPDOWN_OPTIONS = [
  {
    LABEL: "All",
    VALUE: "All",
  },
  {
    LABEL: "Accumulator Year-to-Date",
    VALUE: "Accumulator Year-to-Date",
  },
  {
    LABEL: "Date of Service",
    VALUE: "Date of Service",
  },
];

export const PLAN_YEAR_DROPDOWN_OPTIONS = [
  {
    LABEL: "All",
    VALUE: "All",
  },
  {
    LABEL: "Plan Year-to-Date",
    VALUE: "Plan Year-to-Date",
  },
  {
    LABEL: "Date of Service",
    VALUE: "Date of Service",
  },
];

export const CLAIM_TYPE_DROPDOWN_OPTIONS = [
  {
    LABEL: "All",
    VALUE: "All",
  },
  {
    LABEL: "Medical Only",
    VALUE: "Provider",
  },
  {
    LABEL: "Pharmacy Only",
    VALUE: "Pharmacy",
  },
];

export const CLAIMS_SORT_OPTIONS = [
  {
    LABEL: "Date of Service: New to Old",
    VALUE: "date-",
  },
  {
    LABEL: "Date of Service: Old to New",
    VALUE: "date+",
  },
];

export enum CARE_HISTORY_CLAIM_TIME_PERIOD {
  ALL = "All",
  DATE_RANGE = "Date Range",
}

export enum VISIT_DETAILS_CLAIM_TYPE {
  MEDICAL = "Provider",
  PHARMACY = "Pharmacy",
}

export const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const MONTH_FULL_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export enum MIME_TYPE {
  PDF = "application/pdf",
}

export const DATA_ACCESS_STATUS = {
  ALLOWED: new Set(["GRANTED", "ACKNOWLEDGE_ACCEPTED"]),
  DENIED: new Set(["DECLINED", "REVOKED", "ACKNOWLEDGE_DECLINED"]),
};

export enum DATA_ACCESS_STATUS_LABEL {
  SHARED = "Your data is shared",
  NOT_SHARED = "Your data is not shared",
  PENDING = "Your data is pending",
}

export enum SELF_DATA_ACCESS_STATUS_LABEL {
  SHARED = "Data shared with you",
  NOT_SHARED = "Data not shared with you",
  PENDING = "Data shared with you- pending your approval",
}

export enum DATA_ACCESS_ALL_STATUS {
  GRANTED = "GRANTED",
  ACKNOWLEDGE_ACCEPTED = "ACKNOWLEDGE_ACCEPTED",
  ACKNOWLEDGE_DECLINED = "ACKNOWLEDGE_DECLINED",
  ACCEPTED = "ACCEPTED",
  DECLINED = "DECLINED",
  REVOKED = "REVOKED",
}

export enum DATA_ACCESS_ACTION_BUTTON_LABEL {
  AUTHORIZE_ACCESS = "Authorize Access",
  REVOKE_ACCESS = "Revert Access",
}

export const DATA_ACCESS_STATUS_TO_ALERT_TYPE_MAPPER = {
  GRANTED: "authGrant",
  REVOKED: "authRevoke",
  ACCEPTED: "authAccept",
  DECLINED: "authDeclined",
  AUTH_GRANT: "authGrantDisclaimer",
  AUTH_REVOKE: "authRevokeDisclaimer",
};

export const DATA_ACCESS_DISCLAIMER_PLACEHOLDER = {
  MEMBER_NAME: "member_name",
};

export enum APP_ALERT_MODULE {
  DATA_ACCESS = "dataAccess",
  PAPERLESS_EOB = "paperlessEob",
}

export const STORAGE_KEY = {
  CACHED_GROUP_DETAIL: "cachedGroupDetail",
  GROUP_DATA: "groupData",
  AUTH_DATA: "authData",
  APP_INSTANCE_ID: "appInstanceId",
  LOCATION: "usr_lcn",
  LOCATION_FETCHED: "user_lcn_fch",
  PRODUCT_CONFIG: "productConfig",
  ALERT_DIALOG_STATUS: "alertDialogStatus",
  APP_INTERACTION: "interaction",
  APP_LOGS: "emergency_logs",
  NOTIFICATION_DISMISSED: "notification_dismissed",
  CHAT_LIABILITY_ALERT_DISMISSED: "chat_liability_alert_dismissed",
};

export enum DEFAULT_NETWORK_TYPE {
  NETWORK_NAME = "IN",
  TIER = "TIER_1",
}

export const PROVIDER_SETTING_TYPE = {
  QUICK_SEARCH: "triggerSearch",
  SEARCH_BY_CATEGORY: "loadSpecialty",
};

export enum PROVIDER_SEARCH_FREETEXT_SEARCH_TITLE {
  PROVIDER_NAME = "Provider Name",
  NPI = "NPI Detail",
  SPECIALITY = "Specialty Detail",
}

export enum PROVIDER_SEARCH_TYPES {
  QUICK_SEARCH = "QuickSearch",
  FREETEXT_SEARCH = "FreeTextSearch",
  SEARCH_BY_SPECIALTY = "SpecialtySearch",
}

export enum PROVIDER_SEARCH_FREETEXT_TYPE {
  PROVIDER = "provider",
  NPI = "npi",
  SPECIALITY = "speciality",
}

export const PROVIDER_SEARCH_RESULT_ITEM_REQUIRED_KEYS = [
  "address",
  "color",
  "disclosure",
  "distance",
  "inNetwork",
  "lat",
  "lon",
  "name",
  "networkTag",
  "npi",
  "phone",
  "pinSymbol",
  "priority",
  "providerMetrics",
  "prvExperience",
  "speciality",
  "state",
  "typeCode",
  "typeDesc",
  "zip",
];

export enum RootWidgetTypes {
  SystemAlert = "SystemAlert",
  Welcome = "Welcome",
  Benefits = "Benefits",
  ProviderSearch = "ProviderSearch",
  CustomProvider = "CustomProvider",
  MedClaims = "MedClaims",
  RxClaims = "RxClaims",
  PublicHealth = "PublicHealth",
  Accumulator = "Accumulator",
  Document = "Document",
  Services = "Services",
  FeedBack = "Feedback",
  DirectMessage = "DirectMessage",
  PCP = "PCP",
  Campaign = "Campaign",
  Chatbot = "Chatbot",
  Share = "Share",
  Survey = "Survey",
  BalancedBill = "Bill",
  FeedbackSurvey = "FeedbackSurvey",
  PLAN = "Plan",
  Video = "Video",
  ProviderSearchPT = "ProviderSearchPT",
  Support = "Support",
  WidgetOptionsPT = "WidgetOptionsPT",
  ProviderSearchByGroup = "ProviderSearchByGroup",
  TaxForm = "TaxForm",
}

export enum WidgetId {
  InsuranceCard = "InsuranceCard",
  AllServices = "AllServices",
  SpecificService = "SpecificService",
  SingleNotification = "SingleNotification",
  GroupNotification = "GroupNotification",
  OneToOneChatMessage = "OneToOneChatMessage",
  QuickProvider = "QuickProvider",
  Disease = "Disease",
  CovidInfo = "CovidInfo",
  Biometrics = "Biometrics",
  RecentVisit = "RecentVisit",
  SingleRecommendation = "SingleRecommendation",
  GroupRecommendation = "GroupRecommendation",
  QuickQuestion = "QuickQuestion",
  HRA = "HRA",
  BalancedBill = "BalancedBill",
  FeedbackSurvey = "FeedbackSurvey",
  PlanEnrollment = "PlanEnrollment",
  ProviderSearchPT = "ProviderSearchPT",
  Support = "Support",
}

export enum VideoProvider {
  YOUTUBE = "YOUTUBE",
  VIMEO = "VIMEO",
  AWS = "AWS",
}

export enum STORAGE {
  LOCAL = "local",
  SESSION = "session",
}

export const IGNORED_ROUTES = [
  { code: "", path: "provider-search" },
  { code: "OEN001", path: "open-enrollment" },
];

export const MENU_IGNORED_ROUTES = [
  { code: "BI009", path: "provider-search-pt" },
  { code: "", path: "provider-search" },
  { code: "OEN001", path: "open-enrollment" },
  { code: "C001", path: "chat" },

  { code: "", path: "ebix" },
  { code: "", path: "hcbb" },
];

export const IGNORED_ROOT_WIDGET_IDS = [
  { rootWidgetId: "DirectMessage" },
  { rootWidgetId: "CustomProvider" },
  { rootWidgetId: "PublicHealth" },
  { rootWidgetId: "CustomServices" },
  { rootWidgetId: "Plan" },
];

export const NEGOTIATED_TYPE = {
  PERCENTAGE: "percentage",
  FEE_SCHEDULE: "fee schedule",
};

export const ID_CARD_KEY_BASE64_KEY = "base64Encoded";

export enum SURVEY_QUESTION_TYPE {
  PARAGRAPH = "paragraph",
  CHECKBOX = "checkbox",
  RADIO = "multiplechoice",
  LINEAR_SCALE = "linearscale",
}

export enum CHATBOT_COMPONENT_NAME {
  ATTACHMENTS = "attachments",
  PROVIDER_DETAILS = "provider-details",
  BENEFIT_DOCUMENT = "benefit-document",
  PLAN_SERVICE_DETAILS = "plan-service-detail",
  MEMBER_SCOPE = "member-scope",
  WIDGET = "widget",
  CONTACT_SUPPORT = "support",
  MEMBER_MEDICATION = "medication",
  MEMBER_SPECIFIC_DETAILS = "member-specific-details",
}

export const CHATBOT_MEMBER_SPECIFIC_COMPONENT = [
  "provider-details",
  "benefit-document",
  "plan-service-detail",
  "member-scope",
  "support",
  "medication",
  "member-specific-details",
];

export const CHATBOT_DEFAULT_REPLY =
  "We couldn't answer your query. Please contact your customer support.";

export const USERNAME_REQUIREMENTS_MESSAGE =
  "Username must contain 8-20 characters.";

export const PASSWORD_REQUIREMENTS_MESSAGE =
  "Password must contain at least 8 characters including uppercase, lowercase, numbers, special characters, and must not contain spaces.";

export const EOB_VERBIAGE = {
  EOB_PENDING: "Claim finalized EOB pending.",
};

export enum AlignError {
  START = "start",
  END = "end",
  CENTER = "center",
}

export const APPSIDE_MSG = {
  UPLOAD_SUCCESS: "Upload Successful",
  UPLOAD_FAILED: "Upload Failed",
  DELETE_SUCCESS: "Deleted Successfully",
};

export enum REGISTRATION_FIELDS {
  USERNAME = "username",
  PASSWORD = "password",
  CONFIRM_PASSWORD = "confirmPassword",
  FIRST_NAME = "firstName",
  LAST_NAME = "lastName",
  DOB = "dob",
  ZIP = "zip",
  EMAIL = "email",
  PHONE = "phone",
  MEMBER_ID = "employeeId",
  SSN = "ssnSuffix",
}

export const POS_LABEL_MAPPER: { [key: string]: string } = {
  TELEMEDICINE: "Telemedicine",
  OFFICE: "Office Visit",
  OUTPATIENT: "Outpatient",
  IN_PATIENT: "Inpatient",
  EMERGENCY_ROOM: "Emergency Room",
  OTHERS: "Others",
  PHARMACY: "Pharmacy",
};

export const RELATIONSHIP = {
  DEPENDENT: "DEPENDENT",
  OTHERS: "OTHERS",
};

export enum COVERAGE_CODE {
  MEDICAL = "MED",
  DENTAL = "DEN",
  VISION = "VIS",
  ORTHODONTIA = "ORT",
}

export const coverageCodeList = ["MED", "DEN", "VIS", "ORT"];

// TODO: REMOVE AFTER GLOBAL PLAN COVERAGE STABLIZES
export const planCoverageEnabledRoutes = [
  // ScreenRouteNames.MY_BENEFIT.path,
  // ScreenRouteNames.CARE_HISTORY.path,
  // ScreenRouteNames.SPEND_SUMMARY.path,
  // ScreenRouteNames.ID_CARD.path,
];

export enum MEMBER_TYPE {
  SELF = "Self",
  FAMILY = "Family",
}

export enum SPEND {
  MAXIMUM = 9000000000,
}

export enum MEMBER_STATUS {
  ACTIVE = "ACTIVE",
  TERMED = "TERMED",
}

export const LOGIN_SUPPORT_CHATBOT_ACTIONS = {
  LOCATE_ACCOUNT: "locateAccount",
};

export const alwaysAccessiblePaths = [
  `/${ScreenRouteNames.HOME.path}`,
  `/${ScreenRouteNames.NOTIFICATION.path}`,
  `/${ScreenRouteNames.SURVEY.path}`,
];

export const USER_MODE = {
  MEMBER: "member",
  GUEST: "guest",
};

export const PROVIDER_SEARCH_INITIATING_SOURCE = {
  SCREEN: "provider-search",
  WIDGET: "widget",
};

export const RELATION = {
  EMPLOYEE: "employee",
  SUBSCRIBER: "subscriber",
};
